import { useParams } from 'react-router-dom';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import UpdateIcon from '@mui/icons-material/Update';
import WifiTetheringIcon from '@mui/icons-material/WifiTethering';
import { Box, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { format } from 'date-fns';
import { EstateWorkItem, useEstateNote_GetPublishedEstateNotesFromWorkItemQuery } from 'queries';

import Spinner from 'common/spinner';
import { NotificationSeverity, showAlert } from 'utils/notification-manager';

import AssignedTaskDocumentGenerationDetails from './assigned-task-document-generation-details';
import AssignedTaskStatus from './assigned-task-status';

const useStyles = makeStyles(() => ({
  container: {
    padding: '20px',
    color: '#1C2541',
  },
  headerText: {
    display: 'flex',
    alignItems: 'center',
  },
  headerIcons: {
    marginRight: '8px',
  },
  columnHeader: {
    width: '200px',
  },
  column: {
    display: 'flex',
    width: '100%',
    marginBottom: '40px',
  },
  noteColumn: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    marginBottom: '10px',
  },
  columnBody: {
    display: 'flex',
  },
  bodyText: {
    width: '100%',
    overflow: 'hidden',
    whiteSpace: 'pre-wrap',
    fontSize: '14px',
    lineHeight: '22px',
  },
  lastUpdatedText: {
    color: '#596674',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
}));

type Props = {
  estateWorkItem: EstateWorkItem;
};

const AssignedTaskActivity = ({ estateWorkItem }: Props) => {
  const classes = useStyles();
  const { portalId } = useParams<Record<string, string>>();

  const { data: publishedEstateNotesData, loading: publishedEstateNotesLoading } =
    useEstateNote_GetPublishedEstateNotesFromWorkItemQuery({
      variables: {
        workItemId: estateWorkItem.id,
        portalId: portalId,
      },
      onError: (e) => {
        showAlert({
          message: 'Error getting published estate notes from work item: ' + e.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });

  if (!publishedEstateNotesData || publishedEstateNotesLoading) {
    return <Spinner />;
  }

  const isSelfService =
    estateWorkItem.autoDocumentGeneration &&
    (estateWorkItem.documentGenerationTemplateId ||
      estateWorkItem.conditionalTemplateQuestionSetId);

  const publishedEstateNotes = publishedEstateNotesData.data || [];

  return (
    <div className={classes.container}>
      <div className={classes.column}>
        <div className={classes.columnHeader}>
          <Typography className={classes.headerText}>
            <PlaylistAddCheckIcon className={classes.headerIcons} fontSize="small" />
            Task
          </Typography>
        </div>
        <div
          className={classes.columnBody}
          dangerouslySetInnerHTML={{
            __html: estateWorkItem?.workText || '',
          }}
        ></div>
      </div>
      {!isSelfService && (
        <div className={classes.column}>
          <div className={classes.columnHeader}>
            <Typography className={classes.headerText}>
              <WifiTetheringIcon className={classes.headerIcons} fontSize="small" /> Status
            </Typography>
          </div>
          <div className={classes.columnBody}>
            <AssignedTaskStatus
              estateWorkItemStatus={estateWorkItem.status}
              estateWorkItemSubStatus={estateWorkItem.subStatus}
            />
          </div>
        </div>
      )}

      {isSelfService && (
        <div className={classes.column}>
          <div className={classes.columnHeader}>
            <Typography className={classes.headerText}>
              <ContentCopyIcon className={classes.headerIcons} fontSize="small" /> Documents for
              Review
            </Typography>
          </div>
          <div className={classes.columnBody}>
            <AssignedTaskDocumentGenerationDetails estateWorkItem={estateWorkItem} />
          </div>
        </div>
      )}

      {publishedEstateNotes.length === 0 && (
        <div className={classes.column}>
          <div className={classes.columnHeader}>
            <Typography className={classes.headerText}>
              <UpdateIcon className={classes.headerIcons} fontSize="small" />
              Task Update
            </Typography>
          </div>
          <div className={classes.columnBody}>
            <Box>
              <Typography className={classes.bodyText}>No notes</Typography>
            </Box>
          </div>
        </div>
      )}
      {publishedEstateNotes?.map((estateNote, index) => {
        const isFirstNote = index === 0;

        const noteDate = estateNote.publishDate ?? estateNote.updatedAt;
        const date: string = format(Date.parse(noteDate.toString()), 'LLL d y');
        const time: string = format(Date.parse(noteDate.toString()), 'h:mmaaa');

        return (
          <div className={classes.noteColumn} key={estateNote.id}>
            <div className={classes.columnHeader}>
              {isFirstNote && (
                <Typography className={classes.headerText}>
                  <UpdateIcon className={classes.headerIcons} fontSize="small" />
                  Task Update
                </Typography>
              )}
            </div>
            <div className={classes.columnBody}>
              <Box>
                <Typography className={classes.bodyText}>
                  <div dangerouslySetInnerHTML={{ __html: estateNote.note! }}></div>
                </Typography>
                <Typography className={classes.lastUpdatedText}>
                  {date} {time} <AccessTimeIcon className={classes.headerIcons} fontSize="small" />
                </Typography>
              </Box>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default AssignedTaskActivity;
